import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { api, eur } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard, ProductSkeleton } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";

const LIST_KEYS = ["brand", "model", "size", "gender", "color", "material", "shoe_type", "season", "categories"];
const FLAG_KEYS = ["in_stock", "is_new", "on_sale", "bestseller", "exclusive"];
const COLLECTION_META = {
  nike: ["Nike", "Air Force 1 to Vomero. Every family, every colourway."],
  "air-jordan": ["Air Jordan", "From the AJ1 to the AJ13 — the complete Jordan line."],
  "jordan-retro": ["Jordan Retro", "Heritage silhouettes, faithfully re-issued."],
  adidas: ["Adidas", "Terrace classics and modern performance."],
  "new-balance": ["New Balance", "Made for comfort. Worn for culture."],
  puma: ["Puma", "Archive icons with a street edge."],
  asics: ["Asics", "Tech-runner DNA, lifestyle ready."],
  running: ["Running", "Built for the miles."],
  lifestyle: ["Lifestyle", "Everyday rotation essentials."],
  streetwear: ["Streetwear", "The pairs that define the fit."],
};
const EDITS = [["running", "Running"], ["lifestyle", "Lifestyle"], ["streetwear", "Streetwear"], ["jordan-retro", "Jordan Retro"]];

function FilterPanel({ facets, sp, setParam, toggleList, priceDraft, setPriceDraft, commitPrice }) {
  const { t, lang } = useStore();
  const sel = (k) => (sp.get(k) || "").split(",").filter(Boolean);
  const Check = ({ k, v, label, count }) => (
    <label className="flex cursor-pointer items-center gap-3 py-1.5 text-sm">
      <Checkbox checked={sel(k).includes(v)} onCheckedChange={() => toggleList(k, v)} data-testid={`filter-${k}-${v.replace(/\s/g, "-").toLowerCase()}`} className="rounded-none" />
      <span className="flex-1">{label}</span>{count != null && <span className="font-mono text-xs text-ink/40">{count}</span>}
    </label>
  );
  if (!facets) return <div className="skeleton-shimmer h-96" />;
  return (
    <div data-testid="filter-panel">
      <div className="space-y-3 border-b border-concrete pb-5">
        {FLAG_KEYS.slice(0, 3).map((k) => (
          <label key={k} className="flex cursor-pointer items-center justify-between text-sm">
            <span>{t({ in_stock: "inStockOnly", is_new: "newOnly", on_sale: "saleOnly" }[k])}</span>
            <Switch checked={sp.get(k) === "1"} onCheckedChange={(v) => setParam(k, v ? "1" : null)} data-testid={`filter-flag-${k}`} />
          </label>
        ))}
      </div>
      <Accordion type="multiple" defaultValue={["brand", "model", "size", "price"]}>
        {facets.brands.length > 1 && (
          <AccordionItem value="brand"><AccordionTrigger className="eyebrow">{t("brand")}</AccordionTrigger>
            <AccordionContent>{facets.brands.map((b) => <Check key={b.slug} k="brand" v={b.slug} label={b.name} count={b.count} />)}</AccordionContent></AccordionItem>
        )}
        <AccordionItem value="model"><AccordionTrigger className="eyebrow">{t("model")}</AccordionTrigger>
          <AccordionContent className="max-h-72 overflow-y-auto pr-2">{facets.models.map((m) => <Check key={m.name} k="model" v={m.name} label={m.name} count={m.count} />)}</AccordionContent></AccordionItem>
        <AccordionItem value="size"><AccordionTrigger className="eyebrow">{t("size")}</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-4 gap-1.5">
              {facets.sizes.map((s) => (
                <button key={s} onClick={() => toggleList("size", s)} data-testid={`filter-size-${s}`}
                  className={`border py-2 font-mono text-xs transition-colors ${sel("size").includes(s) ? "border-ink bg-ink text-sun" : "border-concrete hover:border-ink"}`}>{s}</button>
              ))}
            </div>
          </AccordionContent></AccordionItem>
        <AccordionItem value="price"><AccordionTrigger className="eyebrow">{t("price")}</AccordionTrigger>
          <AccordionContent className="px-1 pt-2">
            <Slider min={Math.floor(facets.price.min)} max={Math.ceil(facets.price.max)} step={5} value={priceDraft}
              onValueChange={setPriceDraft} onValueCommit={commitPrice} data-testid="filter-price-slider" />
            <div className="mt-3 flex justify-between font-mono text-xs"><span>{eur(priceDraft[0], lang)}</span><span>{eur(priceDraft[1], lang)}</span></div>
          </AccordionContent></AccordionItem>
        <AccordionItem value="gender"><AccordionTrigger className="eyebrow">{t("gender")}</AccordionTrigger>
          <AccordionContent>{facets.genders.map((g) => <Check key={g} k="gender" v={g} label={g} />)}</AccordionContent></AccordionItem>
        <AccordionItem value="color"><AccordionTrigger className="eyebrow">{t("color")}</AccordionTrigger>
          <AccordionContent>{facets.colors.map((c) => <Check key={c} k="color" v={c} label={c} />)}</AccordionContent></AccordionItem>
        {facets.materials?.length > 0 && (
          <AccordionItem value="material"><AccordionTrigger className="eyebrow">{t("material")}</AccordionTrigger>
            <AccordionContent>{facets.materials.map((m) => <Check key={m} k="material" v={m} label={m} />)}</AccordionContent></AccordionItem>
        )}
        {facets.shoe_types?.length > 0 && (
          <AccordionItem value="shoe_type"><AccordionTrigger className="eyebrow">{t("shoeType")}</AccordionTrigger>
            <AccordionContent>{facets.shoe_types.map((m) => <Check key={m} k="shoe_type" v={m} label={m} />)}</AccordionContent></AccordionItem>
        )}
        {facets.seasons?.length > 0 && (
          <AccordionItem value="season"><AccordionTrigger className="eyebrow">{t("season")}</AccordionTrigger>
            <AccordionContent>{facets.seasons.map((m) => <Check key={m} k="season" v={m} label={m} />)}</AccordionContent></AccordionItem>
        )}
        <AccordionItem value="collection"><AccordionTrigger className="eyebrow">{t("collection")}</AccordionTrigger>
          <AccordionContent>{EDITS.map(([s, l]) => <Check key={s} k="categories" v={s} label={l} />)}</AccordionContent></AccordionItem>
        <AccordionItem value="more"><AccordionTrigger className="eyebrow">{t("availability")}</AccordionTrigger>
          <AccordionContent className="space-y-3">
            {["bestseller", "exclusive"].map((k) => (
              <label key={k} className="flex cursor-pointer items-center justify-between text-sm">
                <span>{k === "bestseller" ? t("bestSellers") : t("exclusiveDrops")}</span>
                <Switch checked={sp.get(k) === "1"} onCheckedChange={(v) => setParam(k, v ? "1" : null)} data-testid={`filter-flag-${k}`} />
              </label>
            ))}
          </AccordionContent></AccordionItem>
      </Accordion>
    </div>
  );
}

export default function Shop() {
  const { slug } = useParams();
  const { t } = useStore();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [priceDraft, setPriceDraft] = useState([0, 1000]);
  const cat = sp.get("cat");
  const [catLabel, setCatLabel] = useState(null);
  useEffect(() => {
    if (!cat) { setCatLabel(null); return; }
    api.get("/categories").then((r) => setCatLabel(r.data.flatMap((g) => g.items).find((i) => i.slug === cat)?.label || null));
  }, [cat]);
  const meta = COLLECTION_META[slug] || (catLabel ? [catLabel, ""] : null);
  const title = meta ? meta[0] : sp.get("q") ? `“${sp.get("q")}”` : sp.get("on_sale") ? t("sale") : sp.get("is_new") ? t("newArrivals") : t("allProducts");
  useSeo({ title: meta ? `${meta[0]} Sneakers` : "Shop Sneakers", description: meta ? `${meta[0]} at CASABLANCA. ${meta[1]}` : "Shop premium sneakers at CASABLANCA." });

  const query = useMemo(() => {
    const q = { sort: sp.get("sort") || "newest", limit: 24 };
    if (slug) q.collection = slug;
    ["q", "cat", "min_price", "max_price", ...LIST_KEYS].forEach((k) => sp.get(k) && (q[k] = sp.get(k)));
    FLAG_KEYS.forEach((k) => sp.get(k) === "1" && (q[k] = true));
    return q;
  }, [sp, slug]);
  const qkey = JSON.stringify(query);

  const brandParam = sp.get("brand");
  useEffect(() => {
    api.get("/products", { params: { collection: slug, cat: cat || undefined, limit: 1, with_facets: true, brand: brandParam || undefined } }).then((r) => {
      setFacets(r.data.facets);
    });
  }, [slug, cat, brandParam]);
  useEffect(() => {
    if (!facets) return;
    setPriceDraft([Number(sp.get("min_price") || Math.floor(facets.price.min)), Number(sp.get("max_price") || Math.ceil(facets.price.max))]);
  }, [facets, sp]);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    api.get("/products", { params: { ...JSON.parse(qkey), page: 1 } }).then((r) => { setData(r.data); setItems(r.data.items); }).finally(() => setLoading(false));
  }, [qkey]);

  const loadMore = () => {
    const n = page + 1;
    setLoading(true);
    api.get("/products", { params: { ...query, page: n } }).then((r) => { setItems((x) => [...x, ...r.data.items]); setPage(n); }).finally(() => setLoading(false));
  };

  const setParam = (k, v) => { const n = new URLSearchParams(sp); if (v == null || v === "") n.delete(k); else n.set(k, v); setSp(n, { replace: true }); };
  const toggleList = (k, v) => {
    const cur = (sp.get(k) || "").split(",").filter(Boolean);
    setParam(k, (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]).join(","));
  };
  const commitPrice = ([a, b]) => { const n = new URLSearchParams(sp); n.set("min_price", a); n.set("max_price", b); setSp(n, { replace: true }); };
  const active = [...LIST_KEYS.flatMap((k) => (sp.get(k) || "").split(",").filter(Boolean).map((v) => [k, v])), ...FLAG_KEYS.filter((k) => sp.get(k) === "1").map((k) => [k, null]),
    ...(sp.get("min_price") ? [["price", `${sp.get("min_price")}–${sp.get("max_price")} €`]] : [])];
  const clearAll = () => { const n = new URLSearchParams(); ["q", "cat"].forEach((k) => sp.get(k) && n.set(k, sp.get(k))); setSp(n, { replace: true }); };
  const removeChip = (k, v) => {
    if (k === "price") { const n = new URLSearchParams(sp); n.delete("min_price"); n.delete("max_price"); setSp(n, { replace: true }); } else if (v == null) setParam(k, null); else toggleList(k, v);
  };
  const panelProps = { facets, sp, setParam, toggleList, priceDraft, setPriceDraft, commitPrice };
  const showFamilies = (slug === "nike" || slug === "air-jordan") && facets;

  return (
    <main className="pb-24" data-testid="shop-page">
      <section className="border-b border-concrete">
        <div className="container-x pb-10 pt-14 sm:pt-20">
          <Reveal>
            <p className="eyebrow mb-4 text-ink/50">{slug ? t("collections") : t("shop")}{data && ` · ${data.total} ${t("results")}`}</p>
            <h1 className="font-display text-6xl font-black uppercase leading-[0.85] tracking-tighter sm:text-8xl lg:text-9xl" data-testid="shop-title">{title}</h1>
            {meta?.[1] && <p className="mt-6 max-w-lg text-ink/60">{meta[1]}</p>}
          </Reveal>
          {showFamilies && (
            <div className="no-scrollbar -mx-5 mt-10 flex gap-2 overflow-x-auto px-5" data-testid="model-family-chips">
              <button onClick={() => setParam("model", null)} className={`shrink-0 border px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${!sp.get("model") ? "border-ink bg-ink text-sun" : "border-concrete hover:border-ink"}`}>All</button>
              {facets.models.map((m) => (
                <button key={m.name} onClick={() => setParam("model", sp.get("model") === m.name ? null : m.name)} data-testid={`family-chip-${m.name.replace(/\s/g, "-").toLowerCase()}`}
                  className={`shrink-0 border px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${sp.get("model") === m.name ? "border-ink bg-ink text-sun" : "border-concrete hover:border-ink"}`}>{m.name}</button>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="sticky top-16 z-30 border-b border-concrete bg-sun/90 backdrop-blur-xl lg:top-20">
        <div className="container-x flex h-14 items-center justify-between gap-4">
          <button className="flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-filters-btn">
            <SlidersHorizontal className="h-4 w-4" /> {t("filters")} {active.length > 0 && `(${active.length})`}
          </button>
          <div className="no-scrollbar hidden flex-1 items-center gap-2 overflow-x-auto lg:flex">
            {active.map(([k, v]) => (
              <button key={k + v} onClick={() => removeChip(k, v)} className="flex shrink-0 items-center gap-1.5 bg-concrete px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider" data-testid={`active-chip-${k}`}>
                {v || t({ in_stock: "inStockOnly", is_new: "newOnly", on_sale: "saleOnly", bestseller: "bestSellers", exclusive: "exclusiveDrops" }[k])} <X className="h-3 w-3" />
              </button>
            ))}
            {active.length > 0 && <button onClick={clearAll} className="link-u shrink-0 font-mono text-[11px] uppercase tracking-wider" data-testid="clear-filters-btn">{t("clearAll")}</button>}
          </div>
          <Select value={sp.get("sort") || "newest"} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="w-[200px] rounded-none border-0 font-mono text-xs uppercase tracking-wider shadow-none focus:ring-0" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {[["popular", "sortPopular"], ["newest", "sortNewest"], ["price_asc", "sortPriceAsc"], ["price_desc", "sortPriceDesc"], ["on_sale", "sortOnSale"]].map(([v, k]) => (
                <SelectItem key={v} value={v} data-testid={`sort-option-${v}`}>{t(k)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="container-x mt-10 grid gap-10 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block"><div className="sticky top-40 max-h-[calc(100vh-11rem)] overflow-y-auto pr-3"><FilterPanel {...panelProps} /></div></aside>
        <div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 xl:gap-x-6" data-testid="product-grid">
            {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
            {loading && Array.from({ length: items.length ? 3 : 9 }).map((_, i) => <ProductSkeleton key={`s${i}`} />)}
          </div>
          {!loading && items.length === 0 && (
            <div className="py-24" data-testid="no-results">
              <p className="font-display text-3xl font-black uppercase">{t("noResults")}</p>
              <button className="btn-ink mt-6" onClick={clearAll}>{t("clearAll")}</button>
            </div>
          )}
          {data && page < data.pages && !loading && (
            <div className="mt-16 flex flex-col items-center gap-4">
              <p className="font-mono text-xs text-ink/50">{items.length} / {data.total}</p>
              <div className="h-px w-48 bg-concrete"><div className="h-px bg-ink" style={{ width: `${(items.length / data.total) * 100}%` }} /></div>
              <button className="btn-line mt-2 hover:bg-ink hover:text-sun" onClick={loadMore} data-testid="load-more-btn">{t("loadMore")}</button>
            </div>
          )}
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="bottom" className="flex h-[88vh] flex-col rounded-none p-0" data-testid="mobile-filters-sheet">
          <div className="flex items-center justify-between border-b border-concrete px-5 py-4">
            <SheetTitle className="font-display text-xl font-black uppercase">{t("filters")}</SheetTitle>
            {active.length > 0 && <button onClick={clearAll} className="link-u mr-8 font-mono text-[11px] uppercase">{t("clearAll")}</button>}
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4"><FilterPanel {...panelProps} /></div>
          <div className="border-t border-concrete p-4"><button className="btn-ink w-full" onClick={() => setMobileOpen(false)} data-testid="mobile-filters-apply">{t("showResults")} {data && `(${data.total})`}</button></div>
        </SheetContent>
      </Sheet>
    </main>
  );
}
